import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ViToastService } from '@vi-iot/ui';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';

import {
  AccessList,
  BffInstallationsApi,
  EquipmentApi,
  EquipmentInstallationsResponse,
  GeneralManagementApiPicker,
  GetResponse,
  Installation,
  InstallationItem,
  InstallationItemGateway,
  InstallationItemGatewayDevice,
} from '@vi-iot/client';

import { InstallationsService } from '../../+installations/services/installations.service';
import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_FEATURES } from 'src/app/shared/services/app-insights/events/features.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { InstallationsStore } from '../installations';
import { AuthStore } from '../auth';

import { MapActions } from './map.actions';
import { MAP_LOAD_ERROR_TRANSLATIONS, MapLoadErrors } from './map.model';
import { MapStore } from './map.store';

@Injectable()
export class MapEffects {
  LoadFirstThousandInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.loadFirstThousandInstallations),
      switchMap(() =>
        this.bffInstallationsApi.listInstallations().pipe(
          map((response) =>
            MapActions.loadFirstThousandInstallationsSuccess({
              installations: response.installations,
              cursor: response.cursor,
            })
          ),
          catchError((err) => of(MapActions.loadFirstThousandInstallationsError({ error: err })))
        )
      )
    );
  });

  LoadSecondThousandInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.loadFirstThousandInstallationsSuccess),
      switchMap((action) => {
        if (action.cursor) {
          return this.bffInstallationsApi.listInstallations({ cursor: action.cursor }).pipe(
            map((response) =>
              MapActions.loadSecondThousandInstallationsSuccess({
                installations: response.installations,
                cursor: response.cursor,
              })
            ),
            catchError((err) => of(MapActions.loadSecondThousandInstallationsError({ error: err })))
          );
        } else {
          return of(MapActions.loadSecondThousandInstallationsSuccess({ installations: [] }));
        }
      })
    );
  });

  LoadThirdThousandInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.loadSecondThousandInstallationsSuccess),
      switchMap((action) => {
        if (action.cursor) {
          return this.bffInstallationsApi.listInstallations({ cursor: action.cursor }).pipe(
            map((response) =>
              MapActions.loadThirdThousandInstallationsSuccess({
                installations: response.installations,
              })
            ),
            catchError((err) => of(MapActions.loadThirdThousandInstallationsError({ error: err })))
          );
        } else {
          return of(MapActions.loadThirdThousandInstallationsSuccess({ installations: [] }));
        }
      })
    );
  });

  loadInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.loadThirdThousandInstallationsSuccess),
      concatLatestFrom(() => this.mapStore.state$),
      map(([_action, state]) =>
        MapActions.listInstallationsSuccess({
          installations: state.installations,
        })
      ),
      catchError((err) => of(MapActions.listInstallationsError({ error: err })))
    );
  });

  listLockedInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        MapActions.loadSecondThousandInstallationsError,
        MapActions.loadThirdThousandInstallationsError,
        MapActions.loadThirdThousandInstallationsSuccess
      ),
      switchMap(() =>
        this.generalManagementApiPicker
          .instance()
          .getInstallationsCollection({ onlyWithLockedDevices: true, includeGateways: true })
          .pipe(
            map((response: EquipmentInstallationsResponse) => {
              if (response.data == null) {
                response.data = [];
              }

              return MapActions.listLockedInstallationsSuccess({
                lockedInstallations: response.data,
              });
            }),
            catchError((error) => of(MapActions.listLockedInstallationsError({ error })))
          )
      )
    );
  });

  listInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.listInstallations),
      concatLatestFrom(() => [
        this.installationsStore.installations$,
        this.authStore.isMaintainer$,
      ]),
      delay(100),
      switchMap(([_action, installations, isMaintainer]) => {
        if (installations.length > 0) {
          const actions: Action[] = [
            MapActions.loadMapSuccess({
              installations,
            }),
          ];
          if (this.shouldFocusOnFirstInstallationIfMaintainer(isMaintainer, installations)) {
            actions.push(
              MapActions.focusOnLocationOfFirstInstallation({
                location: {
                  latitude: installations[0].latitude,
                  longitude: installations[0].longitude,
                },
              })
            );
          }

          return actions;
        } else {
          return [MapActions.loadFirstThousandInstallations()];
        }
      }),
      catchError((err) => of(MapActions.listInstallationsError({ error: err })))
    );
  });

  loadMap$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.listInstallationsSuccess),
      concatLatestFrom(() => this.authStore.isMaintainer$),
      switchMap(([{ installations }, isMaintainer]) => {
        // Always update installations
        const actions: Action[] = [
          MapActions.loadMapSuccess({
            installations,
          }),
        ];
        if (this.shouldFocusOnFirstInstallationIfMaintainer(isMaintainer, installations)) {
          actions.push(
            MapActions.focusOnLocationOfFirstInstallation({
              location: {
                latitude: installations[0].latitude,
                longitude: installations[0].longitude,
              },
            })
          );
        }

        return actions;
      }),
      catchError((err) => this.loadMapErrorHandler(err))
    );
  });

  loadInstallation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.loadInstallation),
      tap(() => {
        this.appInsights.logEvent(
          APP_INSIGHTS_FEATURES.GET_INSTALLATION_FEATURES,
          LogLevelEnum.TRACE
        );
      }),
      switchMap((action) =>
        forkJoin([
          this.equipmentApi.getInstallationAccessList(action.installationId),
          this.equipmentApi.getInstallation(action.installationId),
        ]).pipe(
          map(([accessList, installation]) => {
            const installationItem = this.mapApiResponsesToInstallationItem(
              accessList,
              installation
            );

            return MapActions.loadInstallationSuccess({
              installation: installationItem,
            });
          }),
          catchError((error) => {
            this.appInsights.logEvent(
              APP_INSIGHTS_FEATURES.GET_INSTALLATION_FEATURES_ERROR,
              LogLevelEnum.DEBUG,
              { error }
            );

            return this.loadMapErrorHandler(error);
          })
        )
      )
    );
  });

  searchForInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.searchInstallations),
      concatLatestFrom(() => this.mapStore.state$),
      map(([action, state]) =>
        MapActions.searchInstallationsSuccess({
          searchedInstallations: this.installationsService.searchInstallations(
            action.searchParam,
            state.installations
          ),
        })
      )
    );
  });

  selectInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MapActions.selectInstallation),
      concatLatestFrom(() => this.mapStore.state$),
      map(([action, state]) =>
        MapActions.selectInstallationSuccess({
          selectedInstallations: state.installations.filter(
            (item: InstallationItem) =>
              item.latitude === action.selectedInstallation.latitude &&
              item.longitude === action.selectedInstallation.longitude
          ),
        })
      )
    );
  });

  private mapApiResponsesToInstallationItem(
    accessList: GetResponse<AccessList>,
    installation: GetResponse<Installation>
  ): InstallationItem {
    const owner = accessList?.data?.owner;
    const address = installation?.data?.address;
    const installationOwner = `${owner?.firstName} ${owner?.lastName}`;
    const installationAddress = `${address?.street} ${address?.houseNumber}, ${address?.zip}, ${address?.city}`;

    const gateways: InstallationItemGateway[] = installation.data.gateways.map((gateway) => {
      const installationItemGateway: InstallationItemGateway = {
        serial: gateway.serial,
        lastStatusChangedAt: gateway.lastStatusChangedAt,
        devices: gateway.devices.map((device) => {
          const installationItemGatewayDevice: InstallationItemGatewayDevice = {
            deviceType: device.deviceType,
            gatewaySerial: device.gatewaySerial,
            id: device.id,
            image: device.deviceImage,
            imageUrl: null,
            marketingName: device.marketingName,
            roles: device.roles,
            serial: device.boilerSerial,
            status: device.status,
          };

          return installationItemGatewayDevice;
        }),
        aggregatedStatus: gateway.aggregatedStatus,
      };

      return installationItemGateway;
    });

    return {
      id: installation.data.id,
      location: installationAddress,
      latitude: installation.data.address.geolocation.latitude,
      longitude: installation.data.address.geolocation.longitude,
      ownerName: installationOwner,
      gateways,
      installationType: installation.data.installationType,
    };
  }

  private loadMapErrorHandler(error: any): Observable<Action> {
    this.toastService.error({
      message: this.translateService.instant(
        MAP_LOAD_ERROR_TRANSLATIONS[error.errorType] ??
          MAP_LOAD_ERROR_TRANSLATIONS[MapLoadErrors.LoadingError]
      ),
    });

    return of(MapActions.loadMapError({ error }));
  }

  private shouldFocusOnFirstInstallationIfMaintainer(
    isMaintainer: boolean,
    installations: InstallationItem[]
  ): boolean {
    return (
      isMaintainer &&
      installations?.length > 0 &&
      !!installations[0]?.latitude &&
      !!installations[0]?.longitude
    );
  }

  constructor(
    private actions$: Actions,
    private bffInstallationsApi: BffInstallationsApi,
    private equipmentApi: EquipmentApi,
    private mapStore: MapStore,
    private installationsStore: InstallationsStore,
    private installationsService: InstallationsService,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private authStore: AuthStore,
    private appInsights: AppInsightsMonitoringService,
    private generalManagementApiPicker: GeneralManagementApiPicker
  ) {}
}
